import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { ArchivedChip, RestoreOrDeleteButton } from '../../common/components';
import type { ActivityType } from '../../../types/activityType';
import { getOperationTypeLabel, getOperationTypeColor } from '../../../utils/operationTypeUtils';

interface ActivityTypeListProps {
  activityTypes: ActivityType[];
  onDelete?: (activityTypeId: number) => void;
  onRestore?: (activityTypeId: number) => void;
  onEdit?: (activityType: ActivityType) => void;
}

export default function ActivityTypeList({
  activityTypes,
  onDelete,
  onRestore,
  onEdit,
}: ActivityTypeListProps) {
  return (
    <Box>
      {activityTypes.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          Типи активностей не знайдено
        </Typography>
      ) : (
        <List>
          {activityTypes.map((activityType) => {
            const isArchived = !!activityType.deactivatedDate;

            return (
            <Paper
              key={activityType.id}
              elevation={1}
              sx={{
                mb: 2,
                '&:hover': { elevation: 3 },
              }}
            >
              <ListItem
                secondaryAction={
                  <Box>
                    {onEdit && !isArchived && (
                      <IconButton
                        edge="end"
                        aria-label="edit"
                        onClick={() => onEdit(activityType)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                    <RestoreOrDeleteButton
                      archived={isArchived}
                      edge="end"
                      onDelete={onDelete ? () => onDelete(activityType.id) : undefined}
                      onRestore={onRestore ? () => onRestore(activityType.id) : undefined}
                    />
                  </Box>
                }
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="h6"
                        component="span"
                        color={isArchived ? 'text.secondary' : 'text.primary'}
                      >
                        {activityType.name}
                      </Typography>
                      <Chip
                        label={getOperationTypeLabel(activityType.operationType)}
                        color={getOperationTypeColor(activityType.operationType)}
                        size="small"
                      />
                      {isArchived && <ArchivedChip />}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Тип операції: {activityType.operationType}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          Обов'язкові поля:
                        </Typography>
                        {activityType.requiredFields.clientRequired && (
                          <Chip label="Клієнт" size="small" variant="outlined" />
                        )}
                        {activityType.requiredFields.providerRequired && (
                          <Chip label="Постачальник" size="small" variant="outlined" />
                        )}
                        {activityType.requiredFields.stockRequired && (
                          <Chip label="Склад" size="small" variant="outlined" />
                        )}
                        {!activityType.requiredFields.clientRequired && 
                         !activityType.requiredFields.providerRequired && 
                         !activityType.requiredFields.stockRequired && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            немає
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
            </Paper>
            );
          })}
        </List>
      )}
    </Box>
  );
}
